import {
  ApiJob,
  ApiJobContent,
  ApiJobNote,
  JobEvent,
  JobWarning,
  ListedJob,
} from "@common/api/v1.1/types/jobs";
import { ApiUser } from "@common/api/v1.1/types/users";

export interface Actions {
  abortJob: (jobId: string) => void;
  addNote: (jobId: string, content: string) => Promise<void>;
  setActiveJob: (jobId: string) => void;
  clearActiveJob: () => void;
  closeSidebar: () => void;
  deleteNote: (jobId: string, noteId: string) => Promise<void>;
  fetchNotes: (jobId: string) => Promise<Array<ApiJobNote>>;
  hideMessage: (m: Message) => void;
  loadJob: (jobId: string, listRoles: Array<string>, review: boolean) => Promise<void>;
  loginByForm: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => void;
  loginRequestFailed: () => void;
  logout: () => void;
  removeJobFromList: (jobId: string) => void;
  refreshCheckout: () => Promise<void>;
  resolveWarning: (warning: JobWarning) => Promise<void>;
  showMessage: (
    contents: React.ReactElement,
    title: string,
    displaySeconds?: number,
    icon?: Message["icon"],
  ) => void;
  toggleActiveKey: () => void;
  toggleSidebar: () => void;
  updateJobs: (jobs: Array<ListedJob>, action: JobEvent | null) => void;
  updateNote: (jobId: string, noteId: string, content: string) => Promise<ApiJobNote>;
}

export enum AuthState {
  initial,
  notLoggedIn,
  loggingInByStorage,
  loggingInByForm,
  loggedIn,
  requestFailed
}

type InitialAuthState = Readonly<{
  state: AuthState.initial;
}>;
type NotLoggedInAuthState = Readonly<{
  state: AuthState.notLoggedIn;
}>;
type LoginRequestFailedState = Readonly<{
  state: AuthState.requestFailed;
}>;
type LoggingInByFormState = Readonly<{
  email: string;
  password: string;
  state: AuthState.loggingInByForm;
}>;
type LoggingInByStorageState = Readonly<{
  authString: string;
  token: string;
  state: AuthState.loggingInByStorage;
}>;
export type LoggedInState = Readonly<{
  activeKey: "live" | "test";
  authString: string;
  state: AuthState.loggedIn;
  token: string;
  user: ApiUser;
}>;

export enum JobState {
  aborted,
  failed,
  initial,
  loading,
  ready,
}
type AbortedJobState = Readonly<{
  jobId: string;
  state: JobState.aborted;
}>;
type FailedJobState = Readonly<{
  error: string;
  jobId: string;
  state: JobState.failed;
}>;
type InitialJobState = Readonly<{
  state: JobState.initial;
}>;
type LoadingJobState = Readonly<{
  authString: string;
  jobId: string;
  state: JobState.loading;
}>;
type ReadyJobState = Readonly<{
  authString: string;
  contents: Array<ApiJobContent>;
  job: ApiJob;
  state: JobState.ready;
}>;

export interface Message {
  content: React.ReactElement;
  displaySeconds?: number;
  icon?: "success" | "danger" | "info" | "warning";
  id: string;
  title: string;
}

export type State = Readonly<{
  auth:
    | InitialAuthState
    | NotLoggedInAuthState
    | LoggingInByFormState
    | LoggingInByStorageState
    | LoggedInState
    | LoginRequestFailedState;
  job: AbortedJobState | FailedJobState | InitialJobState | LoadingJobState | ReadyJobState;
  activeJobId?: string;
  jobs: {
    [jobId: string]: {
      data: ListedJob;
      override: Partial<ListedJob> | undefined;
    };
  };
  messages: Array<Message>;
  sidebarVisible: boolean;
}>;

export type ChildrenArgs = Readonly<{
  actions: {
    checkinJob: () => Promise<void>;
    resolveWarning: (warning: JobWarning) => Promise<void>;
    showMessage: Actions["showMessage"];
    saveContent: (
      role: string,
      order: number,
      content: string,
      isNew: boolean,
      type?: string,
      name?: string,
      showMessage?: boolean,
    ) => Promise<void>;
  };
  auth: Omit<Omit<LoggedInState, "state">, "activeKey">;
  contents: ReadyJobState["contents"];
  job: ReadyJobState["job"];
  noteCount: number;
  notes: React.ReactElement;
  sidebarVisible: boolean;
}>;

export const DEFAULT_UI_STATE: State = {
  auth: {
    state: AuthState.initial,
  },
  job: {
    state: JobState.initial,
  },
  jobs: {},
  messages: [],
  sidebarVisible: false,
};

export const ITAP_USER_EMAIL = "itapuser@innodata.com";
export const ITAP_BOT_EMAIL = "@itapbot.com";